// The composer. A multi-line textarea that auto-grows up to a max
// height, with the send/newline keyboard grammar the brief pins:
// Enter sends, Shift+Enter inserts a newline. Empty / whitespace-only
// input keeps the send button disabled.
//
// The formatting toolbar sits on top of the textarea: bold, italic,
// inline code, code block, block quote, link — each 24×24, 4px gap,
// 32px row. A trailing preview-toggle button reveals a formatted
// preview pane beneath the textarea that mirrors timeline rendering.
// Toolbar buttons wrap the current selection (or insert a placeholder
// when nothing is selected); pressing the same button on an already-
// wrapped selection unwraps it. Keyboard shortcuts mirror the buttons:
// Cmd/Ctrl+B (bold), Cmd/Ctrl+I (italic), Cmd/Ctrl+E (inline code),
// Cmd/Ctrl+Shift+. (quote).

import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from "react";
import {
  ArrowUp,
  Bold,
  Code,
  Eye,
  EyeOff,
  Italic,
  Link as LinkIcon,
  Quote,
  SquareCode,
} from "lucide-react";
import { useAutoResizeTextarea } from "@nurion/ui/use-auto-resize-textarea";

import { LinkDialog } from "~/components/LinkDialog";
import { FormattingPreview } from "~/components/FormattingPreview";
import { IconTooltip } from "~/components/IconTooltip";
import { SlashPalette } from "~/components/SlashPalette";
import { SendLaterMenu } from "~/components/SendLaterMenu";
import { isMacHost } from "~/lib/platform";
import { sendWithTransition } from "~/lib/send-transition";
import {
  applyBlockQuote,
  applyBold,
  applyCodeBlock,
  applyInlineCode,
  applyItalic,
  insertLink,
} from "~/lib/markdown-ops";

import { AutocompleteMenu, type AutocompleteMenuRef } from "~/components/AutocompleteMenu";
import {
  COMPOSER_TEXTAREA_MAX_HEIGHT_VAR,
  COMPOSER_TEXTAREA_MIN_HEIGHT_VAR,
} from "~/lib/composer-sizing";
import type { QuoteComposerHandle } from "~/lib/quote";
import type { Client } from "~/lib/matrix";
import {
  realignMentions,
  serialiseFormattedBody,
  shiftMentions,
  useAutocomplete,
  ROOM_MENTION_MXID,
  type AutocompleteTrigger,
  type MentionEdit,
  type MentionToken,
} from "~/hooks/useAutocomplete";
import type { Suggestion } from "~/lib/autocomplete-providers";
import {
  OWN_AGENT_CHANGED_COPY,
  OWN_AGENT_UNAVAILABLE_COPY,
  SCHEDULING_NOT_SUPPORTED_COPY,
  type OwnAgentScope,
} from "~/hooks/useOwnAgent";
import {
  argsText as parseSlashArgsText,
  commandName,
  list as listSlashCommands,
  match as matchSlashCommand,
  matchWith,
  type SlashCommand,
  type SlashCtx,
} from "~/lib/slash-commands";
import { ICON_STROKE } from "~/lib/icon";

/** Mac-vs-other modifier glyph for tooltip strings. The keyboard
 *  handler accepts both `metaKey` (⌘) and `ctrlKey`, so the visible
 *  hint must match the platform. */
const IS_MAC = isMacHost();
const MOD_LABEL = IS_MAC ? "⌘" : "Ctrl";
const SHIFT_LABEL = IS_MAC ? "⇧" : "Shift";

// Debounce between typing refreshes. The composer must fire at most
// one `onTyping(true)` call per 10s of continuous typing, per the
// brief. The embedder (Timeline) pairs each refresh with Matrix's
// 30s server-side timeout — below the timeout so a "typing → pause
// → resume" pattern still carries the indicator across the gap.
const TYPING_REFRESH_MS = 10_000;

/**
 * Provider contract the composer accepts for inline autocomplete.
 *
 * The composer asks the provider for `{ immediate, fetch }` whenever
 * the active query changes. `immediate` paints the menu without a
 * round-trip; `fetch` resolves with the merged local + server list
 * after the menu's debounce window. The composer never re-runs a
 * fetch for the same query — providers are responsible for their
 * own caching when relevant.
 */
export interface AutocompleteProvider {
  immediate: readonly Suggestion[];
  fetch: () => Promise<Suggestion[]>;
  reservedUnavailable?: boolean;
}

export type AutocompleteProviderResolver = (
  trigger: AutocompleteTrigger,
  query: string,
) => AutocompleteProvider;

/** Debounce window for the server-side fan-out. The brief pins 200ms. */
const AUTOCOMPLETE_FETCH_DEBOUNCE_MS = 200;

function inputEventType(event: Event): string {
  return "inputType" in event && typeof event.inputType === "string" ? event.inputType : "";
}

/**
 * Passed to `onSend` so embedders can gate their own post-send draft
 * clear (`draft.markSent()`) on the same signal the composer uses for
 * its success-clear. `editedSinceSubmit()` reports whether the user
 * changed the composer after this send was submitted — including
 * re-typing the same text during an in-flight send, which body
 * equality cannot detect. Embedders call `markSent` only when it
 * returns `false`.
 */
export interface ComposerSubmitMeta {
  editedSinceSubmit: () => boolean;
}

export interface MessageComposerProps {
  client?: Client | null;
  /** Placeholder; brief: "Message <#room-name>" or "Message <@display>". */
  placeholder?: string;
  /**
   * Called when the user presses Enter with non-whitespace content.
   *
   * `formattedBody` is Matrix-custom HTML the composer assembled from
   * inserted mention pills, or `null` when the body has no
   * formatting. Embedders pass it through to the SDK's `sendText`
   * `formattedBody` parameter.
   *
   * `mentions` is the deduped list of mentioned user MXIDs from the
   * surviving mention pills, for `m.mentions.user_ids`. Empty when no
   * user pills survived.
   *
   * `meta.editedSinceSubmit()` lets the embedder gate `draft.markSent()`
   * on whether the user re-edited during the in-flight send.
   */
  onSend: (
    body: string,
    formattedBody: string | null,
    mentions: string[],
    mentionRoom: boolean,
    meta: ComposerSubmitMeta,
  ) => void | Promise<void>;
  onSchedule?: (row: {
    body: string;
    formattedBody: string | null;
    sendAt: number;
  }) => void | Promise<void>;
  disabled?: boolean;
  /** Auto-focus the textarea on mount. Default true — the composer is
   *  the primary interaction target when a room opens. */
  autoFocus?: boolean;
  /**
   * Slot for the paperclip button. The composer keeps the placement
   * but leaves the wiring to the embedder (which has the
   * `useFileUpload` hook + `roomId`). Omit on surfaces without file
   * upload (thread panel, Storybook).
   */
  attachButton?: ReactNode;
  /**
   * Slot for per-file progress rows. Stacked above the textarea.
   * Pass `undefined` / empty array to hide the bay. The composer
   * only reserves vertical space when `progressRows` has children.
   */
  progressRows?: ReactNode;
  /**
   * Called when the user pastes files into the textarea. The
   * composer stops default clipboard handling only when files are
   * present — plain text paste goes to the textarea normally.
   */
  onPasteFiles?: (files: readonly File[]) => void;
  /**
   * Broadcast typing state. The composer calls `onTyping(true)` at
   * most once per `TYPING_REFRESH_MS` while the user is actively
   * typing, and `onTyping(false)` on submit / clear. Optional so
   * Storybook fixtures can render the composer without wiring the
   * Matrix client. The embedder (Timeline) passes a binding that
   * calls `setTyping(client, roomId, …)`.
   */
  onTyping?: (typing: boolean) => void;
  /**
   * Initial textarea content. Used by Storybook variants (and
   * potentially future "edit-as-draft" flows) to land the composer
   * in a known state without wiring an external store. Defaults
   * to the empty string. Ignored when `value` is provided
   * (controlled mode).
   */
  defaultValue?: string;
  /**
   * Initial preview-toggle state. Storybook flips this on for the
   * preview-related variants; production callers leave it off.
   */
  defaultShowPreview?: boolean;
  /**
   * Controlled value for the textarea. When provided, the composer
   * mirrors React's controlled-textarea contract: every keystroke
   * fires `onValueChange`, and the textarea always renders whatever
   * `value` resolves to. Pass alongside `onValueChange`. When
   * omitted, the composer manages its own internal state via
   * `defaultValue`.
   *
   * `null` signals "still hydrating" — the textarea renders empty but stays
   * editable: any keystroke becomes the value, and a late `loadDraft`
   * result is dropped rather than clobbering it (see `useDraft`).
   */
  value?: string | null;
  /** Companion to `value`. Required for controlled mode. */
  onValueChange?: (next: string) => void;
  /**
   * Resolves trigger + query → autocomplete suggestions. Optional —
   * the composer hides the menu surface entirely when not provided
   * (Storybook fixtures, future read-only views). Embedders wire one
   * resolver that fans out to user / room / emoji providers based on
   * the trigger.
   */
  resolveAutocomplete?: AutocompleteProviderResolver;
  /** Optional empty-state copy per autocomplete trigger. Defaults to "No matches". */
  autocompleteEmptyStateMessages?: Partial<Record<AutocompleteTrigger, string>>;
  /** Executes a slash command selected or submitted from the composer. */
  onSlashCommand?: (cmd: SlashCommand, ctx: Pick<SlashCtx, "input" | "argsText">) => Promise<void>;
  extraCommands?: SlashCommand[];
  showToast?: (message: string) => void;
  onRecallOlder?: () => void;
  ownAgentGuard?: (scope: OwnAgentScope) => boolean;
  /**
   * Touch-first layout for the mobile shell: drops the formatting
   * toolbar, shows the underline composer (no flush box), and sizes
   * the attach/send controls to a >=44px touch target. Pure layout —
   * the send pipeline, autocomplete, and draft contract are unchanged.
   */
  mobile?: boolean;
  ref?: Ref<QuoteComposerHandle>;
}

export function MessageComposer({
  client = null,
  placeholder = "Message",
  onSend,
  onSchedule,
  disabled = false,
  autoFocus: autoFocusProp,
  attachButton,
  progressRows,
  onPasteFiles,
  onTyping,
  defaultValue = "",
  defaultShowPreview = false,
  value: controlledValue,
  onValueChange,
  resolveAutocomplete,
  autocompleteEmptyStateMessages,
  onSlashCommand,
  extraCommands,
  showToast,
  onRecallOlder,
  ownAgentGuard,
  mobile = false,
  ref,
}: MessageComposerProps) {
  // Controlled mode is opted into by passing `value` (anything other
  // than `undefined`). `null` is a controlled value too: the composer
  // renders empty but stays editable, so a first keystroke lands;
  // `useDraft` drops a late load instead of clobbering that content.
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const stateValue = isControlled ? (controlledValue ?? "") : internalValue;
  const isHydrating = isControlled && controlledValue === null;
  // While a send is in flight we visually clear the textarea without
  // touching the persisted draft. Two reasons:
  //  1. A failed send must keep the draft intact — propagating the
  //     optimistic clear into `useDraft.setValue("")` would race the
  //     debounced delete and leave a window where the IDB row is
  //     genuinely empty.
  //  2. The user can start typing the next message during the send
  //     without their input being clobbered by a catch-path restore.
  //
  // The override is only the *visual* value. Reads from the textarea
  // (submit, draft persistence) keep going through `stateValue`,
  // which always reflects the user's true input.
  const [submittingOverride, setSubmittingOverride] = useState<string | null>(null);
  const value = submittingOverride ?? stateValue;
  // Latch the most recent canonical (non-overridden) value so the
  // submit path can read it without waiting for a re-render.
  const stateValueRef = useRef(stateValue);
  stateValueRef.current = stateValue;
  // Monotonic counter bumped by `setValue` — the single funnel every
  // value mutation passes through, including the programmatic user
  // edits (quote insert, autocomplete, slash fill) that never fire the
  // textarea's onChange. The submit path latches this at send time and
  // clears the draft only when it is unchanged on success, which
  // distinguishes "unchanged" from "edited back to the same value
  // during the in-flight send" — a distinction body equality cannot
  // make. The submit success/failure clears also bump it, but only
  // after the success gate has read it, so they never affect the gate.
  const editVersionRef = useRef(0);
  const setValue = useCallback(
    (next: string) => {
      editVersionRef.current += 1;
      if (isControlled) {
        onValueChange?.(next);
      } else {
        setInternalValue(next);
      }
    },
    [isControlled, onValueChange],
  );
  const [showPreview, setShowPreview] = useState(defaultShowPreview);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [sendLaterOpen, setSendLaterOpen] = useState(false);
  // Captured at the moment the link dialog opens so a click outside
  // the textarea (which collapses the selection) doesn't lose the
  // user's "format this selection as a link" intent. Both the range
  // and the selected text travel together so the dialog reads its
  // initial label without depending on render ordering.
  const [linkInitial, setLinkInitial] = useState<{
    start: number;
    end: number;
    text: string;
  } | null>(null);
  // Track the textarea node via a callback ref so the menu's
  // anchor-measurement props re-evaluate when the node mounts /
  // remounts (e.g. inside Suspense). A `useRef` would skip the
  // re-render on first attach and the menu would receive `null`
  // until some unrelated state change forced one.
  const [textareaEl, setTextareaEl] = useState<HTMLTextAreaElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const setTextarea = useCallback((node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    setTextareaEl(node);
  }, []);
  useImperativeHandle(
    ref,
    () => ({
      insertQuote(quote: string) {
        const current = stateValueRef.current;
        const next = current.length > 0 ? `${quote}\n\n${current}` : quote;
        setSubmittingOverride(null);
        setValue(next);
        if (current.length > 0) {
          setMentions((prev) =>
            shiftMentions(prev, {
              start: 0,
              removedLength: 0,
              insertedLength: next.length - current.length,
            }),
          );
        }
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        requestAnimationFrame(() => {
          const end = next.length;
          el.setSelectionRange(end, end);
        });
      },
      focus() {
        textareaRef.current?.focus();
      },
    }),
    [setValue],
  );
  // Mentions inserted via autocomplete are kept here keyed by their
  // textarea range. Native input events shift tokens when edits land
  // before them and drop tokens when edits touch their recorded text.
  // Cleared on send.
  const [mentions, setMentions] = useState<MentionToken[]>([]);
  const pendingMentionEditRef = useRef<{ start: number; end: number; valueLength: number } | null>(
    null,
  );
  const autocompleteMenuRef = useRef<AutocompleteMenuRef | null>(null);
  const sendButtonRef = useRef<HTMLButtonElement | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressNextSendClickRef = useRef(false);
  const schedulingRef = useRef(false);
  // Aria-active-descendant state lifted from the menu so the
  // textarea re-renders when arrow-key navigation flips the
  // highlight. The menu fires `onActiveOptionIdChange` whenever
  // its internal `activeOptionId` flips; the ref couldn't drive a
  // composer render and AT users heard a stale option.
  const [activeOptionId, setActiveOptionId] = useState<string | undefined>(undefined);
  const [slashPaletteForcedOpen, setSlashPaletteForcedOpen] = useState(false);
  const [slashActiveIndex, setSlashActiveIndex] = useState(0);
  // Caret index re-read from `selectionStart` on every selectionchange.
  // The menu uses this to follow the caret as the query grows even
  // across line wraps; the hook still owns the `triggerIndex` for
  // the splice. Initialised at -1 so a first render before mount
  // doesn't accidentally collide with a real caret of 0.
  const [caretIndex, setCaretIndex] = useState<number>(-1);
  // Timestamp of the last `onTyping(true)` refresh. Used to throttle
  // subsequent `true` calls to the brief's 10s cadence without firing
  // a timer — the next keystroke after the window checks the clock.
  const lastTypingTrueAtRef = useRef<number>(0);
  // Tracks whether the last call we made was `true`, so we only fire
  // `onTyping(false)` once when the user clears the input. Without
  // this guard an empty input on mount would still fire `false` once
  // on first render, and a second clear would fire redundantly.
  const typingActiveRef = useRef<boolean>(false);

  // Honour `autoFocus` through an effect rather than the DOM
  // attribute so SSR can skip the focus call. The effect runs once
  // per mount; room changes remount the composer (the Timeline
  // re-keys on `roomId`), so focus returns automatically. In
  // controlled mode the focus call also waits for the hydrated value
  // to land — focusing while the textarea is still empty doesn't hurt
  // correctness, but a caret placed before hydration completes would
  // sit in the wrong position once the persisted body fills in.
  // On a phone, focus raises the keyboard: entering a conversation is for
  // reading, so the field waits for a tap.
  const autoFocus = autoFocusProp ?? !mobile;
  useEffect(() => {
    if (!autoFocus) return;
    if (isHydrating) return;
    textareaRef.current?.focus();
    // Move the caret to the end so a restored draft doesn't anchor
    // at position 0 with the existing body trailing the cursor.
    const el = textareaRef.current;
    if (el && el.value.length > 0) {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  }, [autoFocus, isHydrating]);

  useAutoResizeTextarea(textareaRef, value, {
    min: { name: COMPOSER_TEXTAREA_MIN_HEIGHT_VAR, fallback: 40 },
    max: { name: COMPOSER_TEXTAREA_MAX_HEIGHT_VAR, fallback: 200 },
  });

  const hasContent = value.trim().length > 0;
  const slashName = commandName(value);
  const slashQuery =
    value.startsWith("/") && (!value.includes(" ") || slashPaletteForcedOpen)
      ? (slashName ?? "")
      : "";
  const slashCommands = useMemo(() => {
    const all = [...listSlashCommands(), ...(extraCommands ?? [])];
    if (!slashQuery) return all;
    return all.filter((cmd) => cmd.name.toLowerCase().startsWith(slashQuery));
  }, [slashQuery, extraCommands]);
  const slashPaletteOpen =
    Boolean(onSlashCommand) &&
    !disabled &&
    (slashPaletteForcedOpen || (value.startsWith("/") && !value.includes(" ")));

  // Latch the latest `onTyping` callback in a ref so the effects can
  // call it without listing it as a dep. With `onTyping` in the dep
  // array the unmount cleanup would fire on every parent re-render
  // that produces a new callback identity (Timeline rebuilds the
  // closure on every event), emitting a stale `onTyping(false)`
  // mid-session and leaving `typingActiveRef` mis-aligned. Anchoring
  // on a ref keeps the cleanup tied to actual unmount only.
  const onTypingRef = useRef(onTyping);
  useEffect(() => {
    onTypingRef.current = onTyping;
  }, [onTyping]);

  // Emit typing true when the input has non-whitespace content, false
  // when it clears. Throttled to one `true` call per `TYPING_REFRESH_MS`
  // so a sustained typing session refreshes the Matrix typing timeout
  // (the wrapper pairs each refresh with a 30s server-side timeout)
  // without flooding the homeserver. Depending on `value` (not just
  // `hasContent`) ensures the throttle window is re-evaluated on
  // every keystroke — without it, a >30s typing session would let
  // the server-side state expire even though the user never paused.
  useEffect(() => {
    const cb = onTypingRef.current;
    if (!cb) return;
    if (!hasContent) {
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        lastTypingTrueAtRef.current = 0;
        cb(false);
      }
      return;
    }
    const now = Date.now();
    if (!typingActiveRef.current || now - lastTypingTrueAtRef.current >= TYPING_REFRESH_MS) {
      typingActiveRef.current = true;
      lastTypingTrueAtRef.current = now;
      cb(true);
    }
  }, [value, hasContent]);

  // On unmount only (no deps), clear the typing state so peers don't
  // see a stale indicator for an input that no longer exists. The
  // Matrix server-side timeout would eventually expire the state, but
  // the UX is sharper if we emit `false` immediately on room switch /
  // composer hide. Reads through `onTypingRef` so a parent's callback
  // change between mounts doesn't trigger a false unmount path.
  useEffect(() => {
    return () => {
      if (typingActiveRef.current) {
        const cb = onTypingRef.current;
        if (cb) cb(false);
        typingActiveRef.current = false;
      }
    };
  }, []);

  const ownAliasIsStale = useCallback(
    (tokens: readonly MentionToken[]) =>
      tokens.some(
        (t) =>
          t.ownAgent !== undefined && ownAgentGuard !== undefined && !ownAgentGuard(t.ownAgent),
      ),
    [ownAgentGuard],
  );

  const handleCommitMention = useCallback((token: MentionToken) => {
    setMentions((prev) => [...prev, token]);
  }, []);

  const handleBeforeInput = useCallback((event: FormEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    pendingMentionEditRef.current = {
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? el.selectionStart ?? 0,
      valueLength: el.value.length,
    };
  }, []);

  const handleInput = useCallback((event: FormEvent<HTMLTextAreaElement>) => {
    const el = event.currentTarget;
    if (el.dataset.composing === "true") {
      pendingMentionEditRef.current = null;
      return;
    }
    const range = pendingMentionEditRef.current;
    pendingMentionEditRef.current = null;
    if (!range) return;
    const inputType = inputEventType(event.nativeEvent);
    const selectedLength = Math.max(0, range.end - range.start);
    const insertedLength = Math.max(0, el.value.length - (range.valueLength - selectedLength));
    let edit: MentionEdit | null = null;
    if (inputType.startsWith("insert")) {
      edit = { start: range.start, removedLength: selectedLength, insertedLength };
    } else if (inputType.startsWith("delete")) {
      const removedLength =
        selectedLength > 0 ? selectedLength : Math.max(0, range.valueLength - el.value.length);
      const start =
        selectedLength > 0 || inputType.endsWith("Forward")
          ? range.start
          : range.start - removedLength;
      edit = { start: Math.max(0, start), removedLength, insertedLength: 0 };
    } else if (inputType.startsWith("history")) {
      setMentions((prev) => realignMentions(el.value, prev));
      return;
    }
    if (!edit) {
      setMentions([]);
      return;
    }
    setMentions((prev) => shiftMentions(prev, edit));
  }, []);

  const autocomplete = useAutocomplete({
    textareaRef,
    value,
    onChangeValue: setValue,
    onCommitMention: handleCommitMention,
  });

  // Track the active provider snapshot. We resolve providers
  // synchronously (`immediate` rows) every time `trigger` / `query`
  // changes, then debounce the server-side `fetch`. A monotonic
  // request id discards stale fetches when the user keeps typing.
  const [suggestions, setSuggestions] = useState<readonly Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [reservedUnavailable, setReservedUnavailable] = useState(false);
  const fetchIdRef = useRef(0);
  useEffect(() => {
    const id = ++fetchIdRef.current;
    if (!resolveAutocomplete || !autocomplete.open || !autocomplete.trigger) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      setReservedUnavailable(false);
      return;
    }
    const provider = resolveAutocomplete(autocomplete.trigger, autocomplete.query);
    setSuggestions(provider.immediate);
    setReservedUnavailable(Boolean(provider.reservedUnavailable));
    if (provider.reservedUnavailable) {
      setSuggestionsLoading(false);
      return;
    }
    // No server fan-out when the local snapshot already filled the
    // menu — a server hit can't add anything in the first 10 slots.
    const needsFetch = provider.immediate.length < 10;
    if (!needsFetch) {
      setSuggestionsLoading(false);
      return;
    }
    setSuggestionsLoading(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const merged = await provider.fetch();
          if (id !== fetchIdRef.current) return;
          setSuggestions(merged);
        } catch {
          if (id !== fetchIdRef.current) return;
          // Keep the local-only snapshot rather than blanking — a
          // failed remote should never wipe matches the user can see.
        } finally {
          if (id === fetchIdRef.current) setSuggestionsLoading(false);
        }
      })();
    }, AUTOCOMPLETE_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [resolveAutocomplete, autocomplete.open, autocomplete.trigger, autocomplete.query]);

  const handlePickSuggestion = useCallback(
    (suggestion: Suggestion, opts: { keepOpen: boolean }) => {
      if (suggestion.ownAgent && ownAgentGuard?.(suggestion.ownAgent) === false) {
        showToast?.(OWN_AGENT_CHANGED_COPY);
        return;
      }
      if (suggestion.mentionRoom) {
        autocomplete.select({
          text: suggestion.replacement,
          html: suggestion.replacement,
          mxid: ROOM_MENTION_MXID,
          keepOpen: opts.keepOpen,
        });
        return;
      }
      autocomplete.select({
        text: suggestion.replacement,
        html: suggestion.replacementHtml,
        mxid: suggestion.mxid,
        ownAgent: suggestion.ownAgent,
        keepOpen: opts.keepOpen,
      });
    },
    [autocomplete, ownAgentGuard, showToast],
  );

  const runSlashCommand = useCallback(
    async (slash: SlashCommand, body: string, clearSnapshot: string) => {
      if (!onSlashCommand) return;
      const shouldClearComposer = clearSnapshot.length > 0;
      if (shouldClearComposer) setSubmittingOverride("");
      setSlashPaletteForcedOpen(false);
      if (shouldClearComposer) setMentions([]);
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        lastTypingTrueAtRef.current = 0;
        onTypingRef.current?.(false);
      }
      try {
        await onSlashCommand(slash, { input: body, argsText: parseSlashArgsText(body) });
        setSubmittingOverride(null);
        if (shouldClearComposer && stateValueRef.current === clearSnapshot) setValue("");
      } catch (err) {
        console.error("slash command failed", err);
        setSubmittingOverride(null);
      }
    },
    [onSlashCommand, setValue],
  );

  const submit = useCallback(async () => {
    if (!hasContent || disabled) return;
    if (ownAliasIsStale(mentions)) {
      showToast?.(OWN_AGENT_CHANGED_COPY);
      return;
    }
    const body = stateValueRef.current;
    if (onSlashCommand) {
      const { builtin, agents } = matchWith(body, extraCommands ?? []);
      if (builtin) {
        await runSlashCommand(builtin, body, body);
        return;
      }
      if (agents.length === 1) {
        await runSlashCommand(agents[0]!, body, body);
        return;
      }
      if (agents.length > 1) {
        setSlashPaletteForcedOpen(true);
        showToast?.(`Pick which agent should run /${commandName(body)}`);
        return;
      }
    }
    if (body.trimStart().startsWith("/") && onSlashCommand) {
      setSlashPaletteForcedOpen(true);
      return;
    }
    // Assemble Matrix-custom HTML from the recorded mentions and the
    // current body. `serialiseFormattedBody` drops tokens that have
    // drifted (user edited around them) so we never ship a corrupt
    // pill — the plain body remains the canonical content.
    const mentionTokens = mentions;
    const {
      formattedBody,
      mentions: mentionUserIds,
      mentionRoom,
    } = serialiseFormattedBody(body, mentionTokens);
    // Visual-only optimistic clear — show the textarea empty during
    // the in-flight send so the user gets immediate feedback, but
    // keep the canonical value (and the persisted draft) intact. If
    // the send fails, we drop the override; the body and its draft
    // are still there, ready for retry.
    const clearVisual = () => {
      setSubmittingOverride("");
      setMentions([]);
    };
    // Clear typing immediately on submit. The `value` effect above
    // will also fire on the next render once `value` flips to empty,
    // but emitting here makes the ordering deterministic: peers see
    // "stopped typing" before they see the sent message.
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      lastTypingTrueAtRef.current = 0;
      onTypingRef.current?.(false);
    }
    // Latch the edit version at submit. Any user edit during the
    // in-flight send bumps `editVersionRef` via `setValue`, so the
    // success-clear below and the embedder's `markSent` gate both skip
    // the clear when the user re-edited — even back to the same text.
    const submittedEditVersion = editVersionRef.current;
    const editedSinceSubmit = () => editVersionRef.current !== submittedEditVersion;
    const startSend = async () => {
      await onSend(body, formattedBody, mentionUserIds, mentionRoom, { editedSinceSubmit });
    };
    // On a phone the text travels into the conversation (see
    // `send-transition`); elsewhere the composer clears in place.
    const sending =
      mobile && textareaEl
        ? sendWithTransition(textareaEl, clearVisual, startSend)
        : (clearVisual(), startSend());
    try {
      await sending;
      // Success — commit the clear, but only if the user hasn't edited
      // the composer during the in-flight send. In controlled mode
      // `setValue("")` flows through `useDraft`, and the upstream
      // `markSent` path the embedder wires post-send is the canonical
      // delete; `setValue("")` keeps the contract symmetric for
      // uncontrolled callers.
      setSubmittingOverride(null);
      if (!editedSinceSubmit()) {
        setValue("");
      }
    } catch (err) {
      console.error("composer send failed", err);
      // Drop the override and keep `body` recoverable. Three sub-cases:
      //   1. User didn't type during the send → canonical value
      //      still equals `body`; nothing else to do.
      //   2. User typed before any input arrived → canonical value
      //      was reset to their new text via onChange. Prepending
      //      `body` keeps the failed message recoverable without
      //      losing the new typing — the user can edit / split / re-
      //      send. Better than silently dropping either side.
      //   3. Canonical was somehow cleared (defensive) → restore
      //      `body` outright.
      // The persisted draft re-syncs through `useDraft.setValue`
      // automatically, so the brief's "failed sends keep the draft"
      // contract holds end-to-end.
      setSubmittingOverride(null);
      const current = stateValueRef.current;
      // Restore the mention tokens too, realigned to whatever body the
      // recovery path lands on, so a retry still attaches
      // `m.mentions.user_ids` and re-emits the pills.
      if (current === body || current.length === 0) {
        if (current !== body) setValue(body);
        setMentions(realignMentions(body, mentionTokens));
      } else {
        // Separator: blank line so two distinct messages stay
        // visually separable. The submit-time body comes first
        // because it's the older content and the user expected to
        // see it preserved.
        const restored = `${body}\n\n${current}`;
        setValue(restored);
        // Merge the submit-time tokens (now in the leading body) with
        // any pill the user committed into `current` during the failed
        // send (shifted past the body + blank-line separator).
        setMentions((live) => {
          const shifted = shiftMentions(live, {
            start: 0,
            removedLength: 0,
            insertedLength: body.length + 2,
          });
          return realignMentions(restored, [...mentionTokens, ...shifted]);
        });
      }
    }
  }, [
    hasContent,
    disabled,
    onSend,
    setValue,
    mentions,
    mobile,
    textareaEl,
    onSlashCommand,
    runSlashCommand,
    extraCommands,
    showToast,
    ownAliasIsStale,
  ]);

  const pickSlashCommand = useCallback(
    (cmd: SlashCommand, fillOnly: boolean) => {
      const next = `/${cmd.name}${fillOnly ? " " : ""}`;
      setValue(next);
      setSlashPaletteForcedOpen(false);
      setSlashActiveIndex(0);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(next.length, next.length);
      });
    },
    [setValue],
  );

  const runSelected = useCallback(
    (pick: SlashCommand) => {
      const current = stateValueRef.current;
      const currentSlash = matchSlashCommand(current);
      const currentArgs = parseSlashArgsText(current);
      const body =
        currentSlash?.name === pick.name
          ? current
          : `/${pick.name}${currentArgs ? ` ${currentArgs}` : ""}`;
      const { builtin } = matchWith(body, extraCommands ?? []);
      void runSlashCommand(
        builtin ?? pick,
        body,
        current.trimStart().startsWith("/") ? current : "",
      );
    },
    [extraCommands, runSlashCommand],
  );

  const scheduleFor = useCallback(
    async (sendAt: number) => {
      if (!onSchedule || !hasContent || disabled) return;
      if (schedulingRef.current) return;
      if (mentions.some((t) => t.ownAgent !== undefined)) {
        showToast?.(SCHEDULING_NOT_SUPPORTED_COPY);
        return;
      }
      schedulingRef.current = true;
      const body = stateValueRef.current;
      const { formattedBody } = serialiseFormattedBody(body, mentions);
      try {
        await onSchedule({ body, formattedBody, sendAt });
        setSendLaterOpen(false);
        setMentions([]);
        setValue("");
        if (typingActiveRef.current) {
          typingActiveRef.current = false;
          lastTypingTrueAtRef.current = 0;
          onTypingRef.current?.(false);
        }
      } finally {
        schedulingRef.current = false;
      }
    },
    [disabled, hasContent, mentions, onSchedule, setValue, showToast],
  );

  const openLinkDialog = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setLinkInitial({ start, end, text: el.value.slice(start, end) });
    setLinkDialogOpen(true);
  }, []);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    // Restore focus + the original selection on the textarea so the
    // user can keep typing without an extra click.
    const el = textareaRef.current;
    if (el && linkInitial) {
      el.focus();
      try {
        el.setSelectionRange(linkInitial.start, linkInitial.end);
      } catch {
        /* setSelectionRange throws on non-text inputs in some browsers; safe to ignore */
      }
    }
    // Drop the captured snapshot so a subsequent open re-captures
    // the live selection rather than reusing the stale one.
    setLinkInitial(null);
  }, [linkInitial]);

  const handleInsertLink = useCallback(
    (link: { href: string; text: string }) => {
      const el = textareaRef.current;
      if (!el) {
        setLinkDialogOpen(false);
        setLinkInitial(null);
        return;
      }
      // Reapply the captured selection so the helper splices at the
      // intended position (the dialog focus may have collapsed it).
      if (linkInitial) {
        try {
          el.setSelectionRange(linkInitial.start, linkInitial.end);
        } catch {
          /* ignore */
        }
      }
      el.focus();
      insertLink(el, { href: link.href, text: link.text });
      setLinkDialogOpen(false);
      setLinkInitial(null);
    },
    [linkInitial],
  );

  // Toolbar action runners — they all assume a focused textarea and
  // dispatch through `markdown-ops` so the change rides the native
  // undo stack when available.
  const runAction = useCallback((fn: (el: HTMLTextAreaElement) => void) => {
    const el = textareaRef.current;
    if (!el) return;
    fn(el);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Autocomplete consumes ↑/↓/Enter/Tab/Esc when its menu is
      // open. The menu signals via `handleKeyDown` whether it
      // handled the keystroke — if so, we stop here.
      if (autocomplete.open && autocompleteMenuRef.current?.handleKeyDown(event)) {
        return;
      }
      const mod = event.metaKey || event.ctrlKey;

      if (mod && !event.shiftKey && event.key === ".") {
        event.preventDefault();
        setSlashPaletteForcedOpen((open) => !open);
        setSlashActiveIndex(0);
        return;
      }

      if (slashPaletteOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          if (slashCommands.length > 0) setSlashActiveIndex((i) => (i + 1) % slashCommands.length);
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          if (slashCommands.length > 0) {
            setSlashActiveIndex((i) => (i - 1 + slashCommands.length) % slashCommands.length);
          }
          return;
        }
        if (event.key === "Tab") {
          const pick = slashCommands[slashActiveIndex];
          if (pick) {
            event.preventDefault();
            pickSlashCommand(pick, true);
          }
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setSlashPaletteForcedOpen(false);
          return;
        }
        if (event.key === "Enter") {
          const pick = slashCommands[slashActiveIndex];
          if (pick && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            runSelected(pick);
          }
          return;
        }
      }

      if (
        event.key === "ArrowUp" &&
        onRecallOlder &&
        !autocomplete.open &&
        !slashPaletteOpen &&
        !event.nativeEvent.isComposing &&
        !isHydrating &&
        stateValueRef.current.trim() === ""
      ) {
        event.preventDefault();
        onRecallOlder();
        return;
      }

      if (mod && !event.altKey) {
        // Cmd/Ctrl+B → bold. Idempotent.
        if (!event.shiftKey && (event.key === "b" || event.key === "B")) {
          event.preventDefault();
          runAction(applyBold);
          return;
        }
        // Cmd/Ctrl+I → italic. Idempotent.
        if (!event.shiftKey && (event.key === "i" || event.key === "I")) {
          event.preventDefault();
          runAction(applyItalic);
          return;
        }
        // Cmd/Ctrl+E → inline code. Idempotent.
        if (!event.shiftKey && (event.key === "e" || event.key === "E")) {
          event.preventDefault();
          runAction(applyInlineCode);
          return;
        }
        // Cmd/Ctrl+Enter → open send-later menu instead of sending.
        if (!event.shiftKey && event.key === "Enter") {
          event.preventDefault();
          if (hasContent && onSchedule && !disabled) {
            setSendLaterOpen(true);
          } else {
            void submit();
          }
          return;
        }
        // Cmd/Ctrl+Shift+. → block quote. Some keyboards report the
        // event with `key === ">"` (the shifted glyph) and others with
        // `key === "."` plus `shiftKey`; we accept both.
        if (event.shiftKey && (event.key === "." || event.key === ">")) {
          event.preventDefault();
          runAction(applyBlockQuote);
          return;
        }
      }

      if (event.key !== "Enter") return;
      // Shift+Enter (and Alt+Enter / Opt+Enter on some keyboards, which
      // Shift handles as a newline modifier) inserts a newline.
      if (event.shiftKey) return;
      // IME composition: Enter confirms the candidate, it must not send.
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      void submit();
    },
    [
      submit,
      runAction,
      autocomplete.open,
      hasContent,
      onSchedule,
      disabled,
      slashPaletteOpen,
      slashCommands,
      slashActiveIndex,
      pickSlashCommand,
      runSelected,
      runSlashCommand,
      onRecallOlder,
      isHydrating,
    ],
  );

  const clearLongPressTimer = useCallback(() => {
    if (!longPressTimerRef.current) return;
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  }, []);

  const releaseSendPointer = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  const handleSendPointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (!hasContent || disabled || !onSchedule) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      suppressNextSendClickRef.current = false;
      clearLongPressTimer();
      longPressTimerRef.current = setTimeout(() => {
        longPressTimerRef.current = null;
        suppressNextSendClickRef.current = true;
        setSendLaterOpen(true);
      }, 450);
    },
    [clearLongPressTimer, disabled, hasContent, onSchedule],
  );

  useEffect(() => clearLongPressTimer, [clearLongPressTimer]);

  useEffect(() => {
    setSlashActiveIndex(0);
  }, [slashName]);

  // Caret tracking for the menu's anchor position. `selectionStart`
  // moves on every keystroke, click, and arrow-key — `selectionchange`
  // is the only reliable way to observe it without changing values.
  // No `value` dep: the listener already fires after every input, so
  // re-attaching per keystroke would tear down + re-add the same
  // handler on every render for no behaviour gain.
  useEffect(() => {
    const ta = textareaEl;
    if (!ta) return;
    const update = () => {
      // Only react when the textarea is the active selection target
      // — otherwise a Cmd+A elsewhere would mis-anchor the menu.
      if (document.activeElement !== ta) return;
      setCaretIndex(ta.selectionStart ?? 0);
    };
    update();
    document.addEventListener("selectionchange", update);
    return () => {
      document.removeEventListener("selectionchange", update);
    };
  }, [textareaEl]);

  const showMenu = useMemo(
    () => Boolean(resolveAutocomplete) && autocomplete.open,
    [resolveAutocomplete, autocomplete.open],
  );
  const autocompleteEmptyStateMessage = reservedUnavailable
    ? OWN_AGENT_UNAVAILABLE_COPY
    : autocomplete.trigger
      ? autocompleteEmptyStateMessages?.[autocomplete.trigger]
      : undefined;

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onPasteFiles) return;
      const files = event.clipboardData?.files;
      if (!files || files.length === 0) return;
      // Only intercept when the clipboard actually carries files —
      // leaving the default in place for text paste.
      const collected: File[] = [];
      for (let i = 0; i < files.length; i += 1) {
        const f = files.item(i);
        if (f) collected.push(f);
      }
      if (collected.length === 0) return;
      event.preventDefault();
      onPasteFiles(collected);
    },
    [onPasteFiles],
  );

  // Treat the slot as "has content" whenever it's a non-empty array
  // or any truthy ReactNode. This keeps the progress row's flex-column
  // gap collapsed when the embedder passes `undefined` / `null` / `[]`.
  const hasProgressRows = Array.isArray(progressRows)
    ? progressRows.length > 0
    : Boolean(progressRows);

  return (
    <div
      data-feedback-redact="true"
      style={
        mobile
          ? {
              padding: "var(--chat-space-2) var(--chat-space-4)",
            }
          : {
              padding: "var(--chat-composer-padding)",
              borderTop: "1px solid var(--chat-surface-border)",
            }
      }
    >
      <div
        className="chat-composer-wrapper chat-composer-wrapper--flush"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-upload-row-gap)",
          padding: mobile ? 0 : "var(--chat-space-2)",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        {!mobile && (
          <Toolbar
            disabled={disabled}
            showPreview={showPreview}
            onAction={runAction}
            onOpenLinkDialog={openLinkDialog}
            onTogglePreview={() => setShowPreview((prev) => !prev)}
          />
        )}
        {hasProgressRows && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--chat-upload-row-gap)",
              paddingInline: "var(--chat-space-1)",
            }}
          >
            {progressRows}
          </div>
        )}
        <div
          style={
            mobile
              ? {
                  display: "grid",
                  gridTemplateColumns: "var(--chat-timeline-avatar-column-comfortable) 1fr",
                  gridTemplateAreas: '"attach text"',
                  columnGap: "var(--chat-space-3)",
                  alignItems: "end",
                  position: "relative",
                }
              : {
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "var(--chat-space-2)",
                  position: "relative",
                }
          }
        >
          {attachButton && (
            <div
              className="chat-composer-control-wrap"
              style={
                mobile
                  ? { gridArea: "attach", height: 40, justifyContent: "flex-end" }
                  : { height: "calc(var(--chat-type-body-lg-line) + 2 * var(--chat-space-2))" }
              }
            >
              {attachButton}
            </div>
          )}
          <textarea
            ref={setTextarea}
            value={value}
            onBeforeInput={handleBeforeInput}
            onInput={handleInput}
            onChange={(e) => {
              // If the visual override was active and the user just
              // typed, drop the override so subsequent renders show
              // their input — and reset the canonical value to whatever
              // they typed (the override hid the previous body, so the
              // textarea is now showing fresh user input on top of an
              // empty visual baseline).
              if (submittingOverride !== null) setSubmittingOverride(null);
              setValue(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            // IME composition: stamp the textarea with `data-composing`
            // so the markdown-ops helpers can bail mid-composition
            // rather than discarding the candidate.
            onCompositionStart={(e) => {
              (e.currentTarget as HTMLTextAreaElement).dataset.composing = "true";
            }}
            onCompositionEnd={(e) => {
              (e.currentTarget as HTMLTextAreaElement).dataset.composing = "false";
            }}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            // Stable accessible name — screen readers already read the
            // placeholder separately as a description. The per-room
            // context ("Message #design") is the hint, not the label.
            aria-label="Message"
            // Wire the textarea to the listbox so AT users hear the
            // active option as the highlight moves. Both attributes
            // are guarded on `showMenu` so a closed menu does not
            // leak a stale `aria-expanded="false"` reference.
            role={showMenu ? "combobox" : undefined}
            aria-autocomplete={showMenu ? "list" : undefined}
            aria-expanded={showMenu || undefined}
            aria-controls={showMenu ? autocompleteMenuRef.current?.listboxId : undefined}
            aria-activedescendant={showMenu ? activeOptionId : undefined}
            data-testid="message-composer-textarea"
            style={{
              flex: 1,
              resize: "none",
              border: "none",
              outline: "none",
              background: "transparent",
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              // iOS zooms the page into any input below 16px on focus.
              fontSize: mobile ? 16 : "var(--chat-type-body-lg-size)",
              fontWeight: mobile ? 300 : undefined,
              lineHeight: "var(--chat-type-body-lg-line)",
              minHeight: mobile ? 40 : "var(--chat-composer-textarea-min-height)",
              maxHeight: "var(--chat-composer-textarea-max-height)",
              padding: mobile ? "9px 44px 9px var(--chat-space-4)" : "var(--chat-space-2)",
              ...(mobile
                ? {
                    gridArea: "text",
                    background: "var(--chat-surface-raised)",
                    borderRadius: 20,
                  }
                : {}),
            }}
          />
          <AutocompleteMenu
            ref={autocompleteMenuRef}
            client={client}
            open={showMenu}
            suggestions={suggestions}
            loading={suggestionsLoading}
            emptyStateMessage={autocompleteEmptyStateMessage}
            textarea={textareaEl}
            caretIndex={caretIndex}
            onPick={handlePickSuggestion}
            onClose={autocomplete.close}
            onActiveOptionIdChange={setActiveOptionId}
          />
          <SlashPalette
            open={slashPaletteOpen}
            commands={slashCommands}
            activeIndex={slashActiveIndex}
            textarea={textareaEl}
            onPick={(cmd) => {
              if (cmd.agent) runSelected(cmd);
              else pickSlashCommand(cmd, true);
            }}
          />
          <div
            className="chat-composer-control-wrap"
            style={
              mobile
                ? {
                    gridArea: "text",
                    justifySelf: "end",
                    height: 40,
                    paddingRight: 4,
                    // WebKit paints a scrolling textarea over its grid
                    // sibling; without this Send cannot be tapped once the
                    // field has grown to its maximum height.
                    position: "relative",
                    zIndex: 1,
                  }
                : { height: "calc(var(--chat-type-body-lg-line) + 2 * var(--chat-space-2))" }
            }
          >
            <IconTooltip label="Send">
              <button
                type="button"
                aria-label="Send"
                ref={sendButtonRef}
                className="chat-button"
                disabled={!hasContent || disabled}
                onClick={() => {
                  if (suppressNextSendClickRef.current) {
                    suppressNextSendClickRef.current = false;
                    return;
                  }
                  void submit();
                }}
                // A tap must not take focus from the text field: on a phone
                // that closes the keyboard and moves the whole conversation.
                onMouseDown={mobile ? (event) => event.preventDefault() : undefined}
                onPointerDown={handleSendPointerDown}
                onPointerUp={releaseSendPointer}
                onPointerCancel={releaseSendPointer}
                onPointerLeave={releaseSendPointer}
                style={{
                  width: mobile ? 32 : "var(--chat-composer-send-button-size)",
                  height: mobile ? 32 : "var(--chat-composer-send-button-size)",
                  flexShrink: 0,
                  border: "none",
                  borderRadius: mobile ? "var(--chat-radius-full)" : "var(--chat-radius-md)",
                  background: mobile
                    ? hasContent
                      ? "hsl(var(--secondary) / 0.16)"
                      : "transparent"
                    : hasContent
                      ? "var(--chat-cta-bg)"
                      : "var(--chat-cta-disabled-bg)",
                  color: mobile
                    ? hasContent
                      ? "hsl(var(--secondary))"
                      : "hsl(var(--foreground) / 0.25)"
                    : hasContent
                      ? "var(--chat-cta-fg)"
                      : "var(--chat-cta-disabled-fg)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: hasContent && !disabled ? "pointer" : "default",
                  padding: 0,
                }}
              >
                {mobile ? (
                  <ArrowUp size={18} strokeWidth={ICON_STROKE} aria-hidden />
                ) : (
                  <SendGlyph />
                )}
              </button>
            </IconTooltip>
          </div>
          {sendLaterOpen && onSchedule && (
            <SendLaterMenu
              anchor={sendButtonRef.current}
              onSchedule={(sendAt) => void scheduleFor(sendAt)}
              onClose={() => setSendLaterOpen(false)}
            />
          )}
        </div>
        {showPreview && <FormattingPreview body={value} />}
      </div>
      {linkDialogOpen && (
        <LinkDialog
          initialText={linkInitial?.text ?? ""}
          onClose={closeLinkDialog}
          onInsert={handleInsertLink}
        />
      )}
    </div>
  );
}

interface ToolbarProps {
  disabled: boolean;
  showPreview: boolean;
  onAction: (fn: (el: HTMLTextAreaElement) => void) => void;
  onOpenLinkDialog: () => void;
  onTogglePreview: () => void;
}

/** The 32px formatting-toolbar row at the top of the composer.
 *  Each button is 24×24, 4px gap, with the icon at strokeWidth 1.5
 *  per Nurion's icon convention. Buttons use `onMouseDown` +
 *  `preventDefault` so clicking them doesn't blur the textarea
 *  (which would lose the active selection mid-action). */
function Toolbar({
  disabled,
  showPreview,
  onAction,
  onOpenLinkDialog,
  onTogglePreview,
}: ToolbarProps) {
  const buttons: Array<
    | {
        kind: "action";
        tooltip: string;
        ariaLabel: string;
        icon: ReactNode;
        run: (el: HTMLTextAreaElement) => void;
      }
    | { kind: "custom"; tooltip: string; ariaLabel: string; icon: ReactNode; click: () => void }
  > = [
    {
      kind: "action",
      tooltip: `Bold (${MOD_LABEL}B)`,
      ariaLabel: "Bold",
      icon: <Bold size={14} strokeWidth={ICON_STROKE} />,
      run: applyBold,
    },
    {
      kind: "action",
      tooltip: `Italic (${MOD_LABEL}I)`,
      ariaLabel: "Italic",
      icon: <Italic size={14} strokeWidth={ICON_STROKE} />,
      run: applyItalic,
    },
    {
      kind: "action",
      tooltip: `Inline code (${MOD_LABEL}E)`,
      ariaLabel: "Inline code",
      icon: <Code size={14} strokeWidth={ICON_STROKE} />,
      run: applyInlineCode,
    },
    {
      kind: "action",
      tooltip: "Code block",
      ariaLabel: "Code block",
      icon: <SquareCode size={14} strokeWidth={ICON_STROKE} />,
      run: applyCodeBlock,
    },
    {
      kind: "action",
      tooltip: `Block quote (${MOD_LABEL}${SHIFT_LABEL}.)`,
      ariaLabel: "Block quote",
      icon: <Quote size={14} strokeWidth={ICON_STROKE} />,
      run: applyBlockQuote,
    },
    {
      kind: "custom",
      tooltip: `Link (${MOD_LABEL}K)`,
      ariaLabel: "Insert link",
      icon: <LinkIcon size={14} strokeWidth={ICON_STROKE} />,
      click: onOpenLinkDialog,
    },
  ];

  // Plain `<div>` over `role="toolbar"` — the WAI-ARIA toolbar role
  // implies roving tabindex + arrow-key navigation, which this
  // component intentionally doesn't implement. Buttons stay tabbable
  // via natural document order; the keyboard shortcuts route through
  // the textarea handler instead.
  return (
    <div
      aria-label="Formatting toolbar"
      style={{
        display: "flex",
        alignItems: "center",
        height: "var(--chat-composer-toolbar-height)",
        gap: "var(--chat-composer-toolbar-icon-gap)",
        paddingInline: "var(--chat-space-1)",
      }}
    >
      {buttons.map((btn) => (
        <ToolbarButton
          key={btn.ariaLabel}
          tooltip={btn.tooltip}
          ariaLabel={btn.ariaLabel}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (btn.kind === "action") onAction(btn.run);
            else btn.click();
          }}
        >
          {btn.icon}
        </ToolbarButton>
      ))}
      <div style={{ flex: 1 }} />
      <ToolbarButton
        tooltip={showPreview ? "Hide preview" : "Show formatted preview"}
        ariaLabel={showPreview ? "Hide preview" : "Show formatted preview"}
        ariaPressed={showPreview}
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onTogglePreview}
      >
        {showPreview ? (
          <EyeOff size={14} strokeWidth={ICON_STROKE} />
        ) : (
          <Eye size={14} strokeWidth={ICON_STROKE} />
        )}
      </ToolbarButton>
    </div>
  );
}

interface ToolbarButtonProps {
  tooltip: string;
  ariaLabel: string;
  ariaPressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}

function ToolbarButton({
  tooltip,
  ariaLabel,
  ariaPressed,
  disabled,
  onClick,
  onMouseDown,
  children,
}: ToolbarButtonProps) {
  return (
    <IconTooltip label={tooltip} side="top">
      <button
        type="button"
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        className="chat-button"
        disabled={disabled}
        onClick={onClick}
        onMouseDown={onMouseDown}
        style={{
          width: "var(--chat-composer-toolbar-icon-size)",
          height: "var(--chat-composer-toolbar-icon-size)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          background: "transparent",
          color: "var(--chat-fg-secondary)",
          borderRadius: "var(--chat-radius-sm)",
          cursor: disabled ? "default" : "pointer",
          flexShrink: 0,
          padding: 0,
        }}
      >
        {children}
      </button>
    </IconTooltip>
  );
}

function SendGlyph() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
